import { schemas } from '@weenat/client'
import { isFrostAlert } from '@weenat/client/dist/core/alerts'
import useAlertDetails from '@weenat/client/dist/core/alerts/useAlertDetails'
import { Alert } from '@weenat/client/dist/resources/alerts.types'
import { useIntl } from '@weenat/wintl'
import { Head, useNavigate, useParams } from 'app/routx-router'
import AddAlertTargetsModal from 'app/src/alerts/AddAlertTargetsModal'
import AlertTargetTable from 'app/src/alerts/AlertTargetTable'
import CreateOrEditPredicate from 'app/src/alerts/CreateOrEditPredicate'
import { AlertPhoneNumber } from 'app/src/alerts/PickAlertNotificationMethod'
import PredicateTable from 'app/src/alerts/PredicateTable'
import SaveWeenatNumberTipCard from 'app/src/alerts/SaveWeenatNumberTipCard'
import { ErrorComponent } from 'app/src/errors/ErrorScreen'
import useDisclosure from 'app/src/hooks/useDisclosure'
import useToasts from 'app/src/hooks/useToasts'
import Button from 'app/src/kit/Button'
import ButtonWithConfirm from 'app/src/kit/ButtonWithConfirm'
import Icons from 'app/src/kit/Icons'
import Scrollable from 'app/src/kit/Scrollable'
import SuperForm from 'app/src/kit/SuperForm'
import Text from 'app/src/kit/Text'
import useSetKeyToBreadcrumbsCtx from 'app/src/kit/breadcrumbs/useSetKeyToBreadcrumbsCtx'
import CheckboxField from 'app/src/kit/fields/CheckboxField'
import TextField from 'app/src/kit/fields/TextField'
import ToggleField from 'app/src/kit/fields/ToggleField'
import UseOnChange from 'app/src/kit/fields/UseOnChange'
import LoadingCircle from 'app/src/kit/loaders/LoadingCircle'
import HeaderActions from 'app/src/layouts/HeaderActions'
import isNil from 'lodash-es/isNil'
import noop from 'lodash-es/noop'
import { useCallback } from 'react'

const AlertDetails: FC = () => {
  const { t } = useIntl()
  const nav = useNavigate()
  const { alertId } = useParams()
  const { addSuccessToast } = useToasts()

  const onEditionSuccess = useCallback(() => {
    addSuccessToast(t('alerts.edited', { capitalize: true }))
  }, [addSuccessToast, t])

  const onDeletionSuccess = useCallback(
    () => {
      addSuccessToast(t('alerts.deleted', { capitalize: true }))
      nav('/alerts', { replace: true })
    },
    // Never add nav to the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [addSuccessToast, t]
  )

  const {
    alert,
    deleteAlert,
    deleteAlertRequest,
    editedPredicate,
    handleFormChange,
    handlePredicateAddition,
    handlePredicateDeletion,
    handlePredicateEdition,
    handlePredicateEditionValidation,
    handleTargetDeletion,
    handleTargetEditionConfirmation,
    handleUpdate,
    isTouched,
    replaceAlertRequest,
    sources,
    updatedAlert,
    isLoading,
    selectedTzTargets,
    alertRequest
  } = useAlertDetails(alertId, onEditionSuccess, onDeletionSuccess)

  const {
    isOpen: isPredicateEditionModalOpen,
    open: openPredicateEditionModal,
    close: closePredicateEditionModal
  } = useDisclosure(false)

  const {
    isOpen: isTargetEditionModalOpen,
    open: openTargetEditionModal,
    close: closeTargetEditionModal
  } = useDisclosure(false)

  const handleDelete = useCallback(async () => {
    await deleteAlert()
  }, [deleteAlert])

  const targetsCount = isNil(selectedTzTargets)
    ? 0
    : selectedTzTargets.devices.length + selectedTzTargets.plots.length
  const isAllowingEdition = !alert?.predicates?.['near-gt-RR']

  const status = alertRequest.error?.status

  useSetKeyToBreadcrumbsCtx('alert', alert ? alert.name : '-')

  return (
    <>
      <Head title={alert ? alert.name : '-'} />
      <HeaderActions>
        <Flex $gap={'md'}>
          <Button
            variant='small'
            backgroundColor={isTouched ? 'primary.500' : 'grayscale.300'}
            isDisabled={!isTouched}
            isLoading={replaceAlertRequest.isPending}
            onPress={() => {
              handleUpdate()
            }}
          >
            {t('actions.save_changes')}
          </Button>
          <ButtonWithConfirm
            title={t('alerts.delete', { capitalize: true })}
            variant='small'
            kind='danger'
            IconLeft={Icons.Bin}
            importance='sd'
            onPress={handleDelete}
            isLoading={deleteAlertRequest.isPending}
            isError={deleteAlertRequest.isError}
            isSuccess={deleteAlertRequest.isSuccess}
            isDanger
          >
            {t('alerts.delete', { capitalize: true })}
          </ButtonWithConfirm>
        </Flex>
      </HeaderActions>
      {!isLoading && alert && updatedAlert ? (
        <>
          <Scrollable $pt={'lg'} $maxWidth={980} $mx={'auto'}>
            {/* Name & voice call edition */}
            <Text $fontWeight='bold' $fontSize='md'>
              {t('titles.general_infos')}
            </Text>
            <Box $my='lg'>
              <SuperForm
                schema={schemas.alertCreation.validation}
                initialValues={{
                  isActive: isNil(alert) ? true : alert.isActive,
                  name: alert?.name || '',
                  isVoiceCallEnabled: alert.isVoiceCallEnabled || false
                }}
                onSubmit={noop}
              >
                {({ isValid, values: formValues }) => {
                  return (
                    <>
                      <UseOnChange
                        onChange={(values) => {
                          const newAlert: Alert = {
                            ...updatedAlert,
                            isActive: values.isActive as boolean,
                            name: values.name as string,
                            isVoiceCallEnabled: values.isVoiceCallEnabled as boolean
                          }
                          handleFormChange({ newAlert, isValid })
                        }}
                      />
                      <TextField
                        label={t('models.plot.fields.name.label', { capitalize: true })}
                        name='name'
                      />
                      {isFrostAlert(updatedAlert.predicates) ? (
                        <>
                          <CheckboxField
                            label={t('alerts.paramSetups.fieldLabels.enable_voice_call')}
                            name='isVoiceCallEnabled'
                            $hideErrors
                          />

                          {formValues.isVoiceCallEnabled ? (
                            <Flex $flexDirection='column' $gap='lg' $my='lg'>
                              <SaveWeenatNumberTipCard />
                              <AlertPhoneNumber viewOnly />
                            </Flex>
                          ) : null}
                        </>
                      ) : null}
                      <ToggleField
                        name='isActive'
                        label={
                          formValues.isActive === true
                            ? t('alerts.activated', { capitalize: true })
                            : t('alerts.deactivated', { capitalize: true })
                        }
                      />
                    </>
                  )
                }}
              </SuperForm>
            </Box>
            {/* Predicate List */}
            <Text $fontWeight='bold' $fontSize='md'>
              {t('alerts.validation.events', { capitalize: true })}
            </Text>
            <Box $my='lg'>
              <PredicateTable
                isAllowingEdition={isAllowingEdition}
                predicates={updatedAlert.predicates}
                onPredicateEdition={(predicate) => {
                  handlePredicateEdition(predicate)
                  openPredicateEditionModal()
                }}
                onPredicateDeletion={handlePredicateDeletion}
                onPredicateAddition={() => {
                  handlePredicateAddition()
                  openPredicateEditionModal()
                }}
              />
            </Box>
            {/* Target List */}
            <Text $fontWeight='bold' $fontSize='md'>{`${t('alerts.validation.targets', {
              capitalize: true
            })} ${targetsCount !== 0 ? `(${targetsCount})` : ''}`}</Text>
            {selectedTzTargets && (
              <Box $py='lg'>
                <AlertTargetTable
                  alert={alert}
                  sources={sources}
                  targets={selectedTzTargets}
                  onTargetAddition={() => {
                    openTargetEditionModal()
                  }}
                  onTargetDeletion={handleTargetDeletion}
                />
              </Box>
            )}
          </Scrollable>

          {isPredicateEditionModalOpen && (
            <CreateOrEditPredicate
              alert={alert}
              isOpen={isPredicateEditionModalOpen}
              close={closePredicateEditionModal}
              initialPredicate={editedPredicate}
              disabledSources={sources}
              onValidation={(finalPredicate) => {
                handlePredicateEditionValidation(finalPredicate)
                closePredicateEditionModal()
              }}
            />
          )}

          {isTargetEditionModalOpen && selectedTzTargets && (
            <AddAlertTargetsModal
              isOpen={isTargetEditionModalOpen}
              close={closeTargetEditionModal}
              initialTargets={selectedTzTargets}
              sources={sources}
              alertName={alert.name}
              onConfirm={handleTargetEditionConfirmation}
            />
          )}
        </>
      ) : isLoading ? (
        <LoadingCircle />
      ) : alertRequest.isError && !isNil(status) ? (
        <ErrorComponent status={status} />
      ) : null}
    </>
  )
}

export const Component = AlertDetails
export default AlertDetails
